import { AppBar, Box, Divider, Tab, Tabs, Toolbar, Typography, useTheme } from '@mui/material';
import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { LogoFnl2cHorzC, LogoMark2cIconC } from '@/assets';
import { useAuth } from '@/auth';
import { globalConstants } from '@/shared/duck';
import { muiPalette } from '@/shared/general-theme';
import { useMedia } from '@/shared/hooks';
import { useGlobalContext } from '@/shared/providers';

import { AuthorizationSection, MobileHeader } from './components';
import { HeaderElements } from './duck';

const Header: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { isSmallScreen, isMediumScreen } = useMedia();
  const { currentPage, setCurrentPage } = useGlobalContext();
  const { isLoggedIn } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const [currentTabNumber, setCurrentTabNumber] = React.useState(() => {
    const currentTabIndex = HeaderElements.findIndex(
      (element) => location.pathname.startsWith(element.to),
    );
    return currentTabIndex !== -1 ? currentTabIndex : 0;
  });

  React.useLayoutEffect(() => {
    if (isLoggedIn) {
      const selectedTab = HeaderElements[currentTabNumber];
      if (currentPage !== selectedTab.title) {
        setCurrentPage(selectedTab.title);
      }
    } else {
      setCurrentPage(globalConstants.PAGES.welcome);
    }
  }, [currentTabNumber, currentPage, setCurrentPage, isLoggedIn]);

  const handleChange = async (_e: React.SyntheticEvent, newValue: number) => {
    const selectedTab = HeaderElements[newValue];
    if (selectedTab && location.pathname !== selectedTab.to) {
      setCurrentTabNumber(newValue);
      await navigate(selectedTab.to);
    }

    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };

  const toggleMobileMenu = useCallback(() => setMobileMenuOpen(!mobileMenuOpen), [mobileMenuOpen]);

  return (
    <AppBar
      position='sticky'
      color='inherit'
      elevation={0}
      sx={{
        borderBottom: '1px solid',
        borderColor: muiPalette.divider,
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(12px)',
        zIndex: theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar
        sx={{
          px: { xs: 2, md: 4 },
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            component='div'
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: 800,
              fontSize: '1.5rem',
              letterSpacing: '-0.02em',
              color: muiPalette.primary.main,
            }}
          >
            {isSmallScreen ? (
              <LogoMark2cIconC
                style={{
                  height: '2rem',
                }}
              />
            ) : (
              <LogoFnl2cHorzC
                style={{
                  height: '2rem',
                  cursor: 'default',
                }}
              />
            )}

            <Divider
              orientation='vertical' flexItem sx={{
              mx: 2,
              height: '1.5rem',
              alignSelf: 'center',
              borderColor: muiPalette.divider,
            }}
            />

            <Typography
              variant={isSmallScreen ? 'body1' : 'h6'}
              component='div'
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.02em',
                color: muiPalette.primary.main,
              }}
            >
              PLATYPUS<span style={{ color: muiPalette.tertiary.main }}>.</span>
            </Typography>
          </Box>
        </Box>

        {!isSmallScreen && isLoggedIn && (
          <Tabs
            value={currentTabNumber}
            onChange={handleChange}
            centered
            role='navigation'
            textColor='primary'
            indicatorColor='primary'
          >
            {HeaderElements.map((element) => {
                const Icon = element.icon || '';
                return (
                  <Tab
                    key={element.title}
                    label={isMediumScreen ? '' : element.title}
                    icon={Icon && <Icon />}
                    disabled={!element.isActive}
                    iconPosition='start'
                    sx={{
                      fontWeight: 600,
                    }}
                  />
                );
              },
            )}
          </Tabs>
        )}

        <AuthorizationSection toggleMobileMenu={toggleMobileMenu} />

        {isSmallScreen && isLoggedIn && (
          <MobileHeader
            mobileMenuOpen={mobileMenuOpen}
            toggleMobileMenu={toggleMobileMenu}
            headerElements={HeaderElements}
            handleChangePage={handleChange}
            currentPage={currentPage}
          />
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;